import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";

function formatDate(date) {
  return new Date(date).toLocaleDateString();
}

function ProgressBar({ percent, color }) {
  return (
    <div className="w-full h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
      <div
        className={`h-full rounded-full transition-all duration-700 ease-out ${color} shadow-sm`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      ></div>
    </div>
  );
}

const Dashboard = forwardRef(function Dashboard(props, ref) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = () => {
    setLoading(true);
    api.get('/api/admin/dashboard')
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshDashboard: fetchDashboard
  }));

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) return null;

  if (loading) return <div className="p-8 text-center text-gray-500">Loading dashboard...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!data) return null;

  return (
    <div className="min-h-full h-full bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-gray-900 dark:via-gray-900 dark:to-indigo-950/20">
      <div className="h-full overflow-y-auto pretty-scrollbar">
        <div className="p-8 max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard Overview</h1>
            <p className="text-gray-600 dark:text-gray-400">Monitor your subscription and system usage</p>
          </div>

          {/* Subscription Card */}
          <div className="mb-8">
            <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 shadow-xl shadow-indigo-500/5 dark:shadow-indigo-500/10">
              {/* Subtle background pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-purple-50/30 dark:from-indigo-950/20 dark:via-transparent dark:to-purple-950/10"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-indigo-100/30 via-transparent to-transparent dark:from-indigo-900/20 rounded-full -translate-y-32 translate-x-32"></div>
              
              <div className="relative p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                  {/* Subscription Info */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Active Subscription</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Current plan details</p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{data.activeSubscription?.name || "No Plan"}</div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span>Activated: {formatDate(data.activeSubscription?.activatedAt)}</span>
                        <span>Expires: {formatDate(data.activeSubscription?.expiresAt)}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-full text-sm font-medium text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                        Users: {data.activeSubscription?.subscription?.limits?.userlimit ?? "-"}
                      </div>
                      <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/30 rounded-full text-sm font-medium text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                        Docs: {data.activeSubscription?.subscription?.limits?.documents ?? "-"}
                      </div>
                      <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-full text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        Chats: {data.activeSubscription?.subscription?.limits?.chats ?? "-"}
                      </div>
                    </div>
                    
                    {data.isTrial && (
                      <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-amber-800 dark:text-amber-200 font-medium">Trial: {data.trialDaysLeft} days remaining</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Usage Metrics */}
                  <div className="space-y-4">
                    <div className="bg-white/60 dark:bg-gray-700/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/60 dark:border-gray-600/60">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Users</span>
                        <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{data.userCount}/{data.userLimit}</span>
                      </div>
                      <ProgressBar percent={data.userQuotaPercent} color="bg-gradient-to-r from-indigo-500 to-indigo-600" />
                    </div>
                    
                    <div className="bg-white/60 dark:bg-gray-700/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/60 dark:border-gray-600/60">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Documents</span>
                        <span className="text-lg font-bold text-purple-600 dark:text-purple-400">{data.documentCount}/{data.documentLimit}</span>
                      </div>
                      <ProgressBar percent={data.documentQuotaPercent} color="bg-gradient-to-r from-purple-500 to-purple-600" />
                    </div>
                    
                    <div className="bg-white/60 dark:bg-gray-700/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/60 dark:border-gray-600/60">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chats</span>
                        <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{data.chatCount}/{data.chatLimit}</span>
                      </div>
                      <ProgressBar percent={data.chatQuotaPercent} color="bg-gradient-to-r from-blue-500 to-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Users and Documents List */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Users List */}
            <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 shadow-xl shadow-blue-500/5">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-transparent dark:from-blue-950/20"></div>
              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text:white">Users</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Active user accounts</p>
                  </div>
                </div>
                
                <div className="overflow-hidden rounded-2xl border border-gray-200/60 dark:border-gray-700/60">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50/50 dark:bg-gray-700/30">
                        <tr className="text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          <th className="py-3 px-4">Name</th>
                          <th className="py-3 px-4">Email</th>
                          <th className="py-3 px-4">Joined</th>
                          <th className="py-3 px-4">Chats</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/60">
                        {data.users.map((u, index) => (
                          <tr key={u.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="font-medium text-gray-900 dark:text-white">{u.firstName} {u.lastName}</div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{u.email}</td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{formatDate(u.joinDate)}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                                {u.chatCount}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents List */}
            <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 shadow-xl shadow-purple-500/5">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-transparent to-transparent dark:from-purple-950/20"></div>
              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text:white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Documents</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Uploaded documents</p>
                  </div>
                </div>
                
                <div className="overflow-hidden rounded-2xl border border-gray-200/60 dark:border-gray-700/60">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50/50 dark:bg-gray-700/30">
                        <tr className="text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          <th className="py-3 px-4">Name</th>
                          <th className="py-3 px-4">Uploaded</th>
                          <th className="py-3 px-4">Clauses</th>
                          <th className="py-3 px-4">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/60">
                        {data.documents.map((d, index) => (
                          <tr key={d.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="font-medium text-gray-900 dark:text-white truncate max-w-xs" title={d.originalName}>
                                {d.originalName}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{formatDate(d.uploadDate)}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                                {d.clauseCount}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                d.processingStatus === 'completed' 
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' 
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                              }`}>
                                {d.processingStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Dashboard; 